/**
 * F-047: Mutation hook for rejecting an ordered order (ordered -> rejected).
 *
 * Wraps `rejectOrderTransition`. On success, updates the detail cache and
 * invalidates the store order list plus incoming-order badges. On business
 * failure or network error, fires a toast with the mapped error key.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { mapTransitionError, rejectOrderTransition, useTranslation } from '@dukanti/shared';
import type { RejectTransitionResult } from '@dukanti/shared/types/order';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export interface UseRejectOrderOptions {
  readonly orderId: string;
  readonly storeId: string | null;
}

export function useRejectOrder(
  opts: UseRejectOrderOptions,
): UseMutationResult<RejectTransitionResult, unknown, void> {
  const { orderId, storeId } = opts;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<RejectTransitionResult, unknown, void>({
    mutationFn: () => rejectOrderTransition(supabase, orderId),
    onSuccess: (result) => {
      if (!result.success) {
        const key = mapTransitionError(undefined, result, 'reject');
        toast.error(t(key));
        return;
      }

      if (result.order) {
        queryClient.setQueryData(
          ['admin-order-detail', orderId],
          (old: { order: unknown; items: unknown; events: unknown } | undefined) =>
            old ? { ...old, order: result.order } : old,
        );
      }
      if (storeId) {
        void queryClient.invalidateQueries({ queryKey: ['store-orders', storeId] });
      }
      void queryClient.invalidateQueries({ queryKey: ['incoming-orders-count-user'] });
      toast.success(t('admin.orders.actions.successToasts.rejected'));
    },
    onError: (err) => {
      const key = mapTransitionError(err, undefined, 'reject');
      toast.error(t(key));
    },
  });
}
